import { InformationSection } from '@/components/atoms/informationSection'
import { InformationH2 } from '@/components/atoms/typography'
import PageContentWrapper, {
  PageContentWrapperBackgroundStyle
} from '@/components/organisms/pageContentWrapper'
import useTranslation from 'next-translate/useTranslation'
import CheckOrderForm from '@/components/organisms/checkOrderForm'
import OrderStatusBanner from '@/components/molecules/orderStatusBanner'
import { FC, useEffect, useState } from 'react'
import { OrderStatus, Page, Sdk } from '@/network/graphql.g'
import PackageStatusButton from '@/components/organisms/PackageStatusButton'
import { useRouter } from 'next/router'
import { Routes } from '@/core/routes'
import { makeLoginRouterWithReferrer } from '@/utils/routerHelpers'
import useGraphQl from 'hooks/useGraphQl'
import { is401, isOk, requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { useMhub } from '@/providers/mhubProvider'
import { getMainData } from '@/utils/mainDataHelper'

const CheckOrder: FC = () => {
  const { t } = useTranslation('common')
  const { requestNotifyError } = useGraphQl()
  const [orderStatus, setOrderStatus] = useState<OrderStatus>(null)
  const [email, setEmail] = useState(null)
  const router = useRouter()

  const onSubmit = async (orderId: string, email: string) => {
    const result = await requestNotifyError((sdk: Sdk) => ({
      method: sdk.getOrderStatus,
      variables: { orderId, email },
      successPredicate: (response) =>
        isOk(response?.orderStatus?.responseCode) ||
        is401(response?.orderStatus?.responseCode),
      errorText: () => t('CheckOrder.missingOrderId')
    }))
    if (result.success) {
      if (isOk(result.response?.orderStatus?.responseCode)) {
        setOrderStatus(result.response?.orderStatus)
        setEmail(email)
      } else if (is401(result.response?.orderStatus?.responseCode)) {
        router.push(
          makeLoginRouterWithReferrer(`${Routes.checkOrder}?orderId=${orderId}`)
        )
      }
    } else {
      setOrderStatus(null)
      setEmail(null)
    }
  }

  const { pushCheckOrderPage } = useMhub()
  useEffect(() => {
    pushCheckOrderPage()
  }, [])

  return (
    <PageContentWrapper
      backgroundStyle={PageContentWrapperBackgroundStyle.normal}
    >
      <InformationH2>{t('CheckOrder.title')}</InformationH2>
      {orderStatus && (
        <OrderStatusBanner
          status={orderStatus.orderStatus}
          statusColor={orderStatus.statusColor}
          statusId={orderStatus.orderStatusId}
        />
      )}
      {orderStatus?.isPackageStatusAvailable && (
        <PackageStatusButton
          orderId={orderStatus.orderId}
          email={email}
          mt={15}
        />
      )}
      <InformationSection>
        <CheckOrderForm
          onSubmit={onSubmit}
          orderNumber={(router.query.orderId ?? '').toString()}
        />
      </InformationSection>
    </PageContentWrapper>
  )
}

export default CheckOrder

export const getServerSideProps = withBaseServerSideProps(
  async ({
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<Rest>> => {
    const { response } = await requestThrow401({
      method: sdk.getSeoTags,
      variables: {
        page: Page.CheckOrderStatus,
        withMainNavigation,
        withStore
      }
    })

    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags
      }
    }
  }
)
